import { useState, useCallback, useMemo } from 'react'
import { filter, reject, some, get } from 'lodash'
import useServiceNetworksResources from '../../shared/resources/api/v1/admin/docker/useServiceNetworksResources'
import { useDockerNetworks } from '../../contexts/DockerNetworksContext'

export default function useServiceNetworksState(teamSlug, projectSlug, serviceSlug, currentProjectId) {
  const [serviceNetworks, setServiceNetworks] = useState([])
  const [loading, setLoading] = useState(true)

  const { networks: allNetworks } = useDockerNetworks()

  const {
    all: fetchServiceNetworks,
    create,
    destroy,
  } = useServiceNetworksResources(teamSlug, projectSlug, serviceSlug)

  const load = useCallback(() => {
    setLoading(true)
    return fetchServiceNetworks()
      .then((response) => setServiceNetworks(response.serviceNetworks || []))
      .catch(() => setServiceNetworks([]))
      .finally(() => setLoading(false))
  }, [fetchServiceNetworks])

  const refresh = useCallback(() => {
    return fetchServiceNetworks()
      .then((response) => setServiceNetworks(response.serviceNetworks || []))
      .catch(() => setServiceNetworks([]))
  }, [fetchServiceNetworks])

  const createServiceNetwork = useCallback((data) => {
    return create(data).then(() => refresh())
  }, [create, refresh])

  const destroyServiceNetwork = useCallback((id) => {
    return destroy(id).then(() => refresh())
  }, [destroy, refresh])

  // Available networks: project networks + external networks from other projects - already attached
  const availableNetworks = useMemo(() => {
    // Get networks from current project
    const projectNetworks = filter(allNetworks, (n) => get(n, 'projectId') === currentProjectId)

    // Get external networks from other projects
    const externalNetworksFromOtherProjects = filter(
      allNetworks,
      (n) => get(n, 'external') === true && get(n, 'projectId') !== currentProjectId
    )

    // Combine and filter out already attached
    const allAvailable = [...projectNetworks, ...externalNetworksFromOtherProjects]
    return reject(allAvailable, (n) => some(serviceNetworks, (sn) => get(sn, 'network.id') === n.id))
  }, [allNetworks, currentProjectId, serviceNetworks])

  return {
    serviceNetworks,
    loading,
    availableNetworks,
    load,
    createServiceNetwork,
    destroyServiceNetwork,
  }
}
